import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DollarSign, ArrowLeft, ArrowRight } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import SapSubNav from '@/components/sap/SapSubNav';

const SapCalculatorPage = () => {
  const [countries, setCountries] = useState(15);
  const [vendors, setVendors] = useState(5);
  const [annualSpend, setAnnualSpend] = useState(500);

  const currentTCO = useMemo(() => annualSpend * 1000 + vendors * 38000 + vendors * 15000 + countries * 6500, [countries, vendors, annualSpend]);
  const taxeraTCO = useMemo(() => 85000 + countries * 4200 + countries * 1800, [countries]);
  const savings = currentTCO - taxeraTCO;
  const savingsPct = currentTCO > 0 ? Math.round((savings / currentTCO) * 100) : 0;
  const fmt = (v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : `${Math.round(v / 1000)}K`;

  return (
    <PageLayout>
      <SapSubNav />
      <section className="pb-8 pt-6" data-testid="calculator-hero">
        <div className="max-w-5xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-50 text-blue-600 text-sm font-medium rounded-full mb-4">
            <DollarSign className="w-4 h-4" strokeWidth={1.5} /> TCO Calculator
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-taxera-charcoal leading-[1.1] mb-4">
            What could you <span className="taxera-gradient-text">save?</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl">Configure your compliance footprint and see projected savings with platform consolidation.</p>
        </div>
      </section>

      <section className="pb-20" data-testid="calculator-body">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <label className="flex items-center justify-between text-sm font-semibold text-taxera-charcoal mb-2">Countries requiring compliance <span className="text-taxera-teal font-bold">{countries}</span></label>
                  <input type="range" min={1} max={50} value={countries} onChange={e => setCountries(+e.target.value)} className="w-full accent-taxera-teal" data-testid="tco-countries-slider" />
                  <div className="flex justify-between text-2xs text-slate-400 mt-1"><span>1</span><span>50</span></div>
                </div>
                <div>
                  <label className="flex items-center justify-between text-sm font-semibold text-taxera-charcoal mb-2">Current compliance vendors <span className="text-taxera-teal font-bold">{vendors}</span></label>
                  <input type="range" min={1} max={15} value={vendors} onChange={e => setVendors(+e.target.value)} className="w-full accent-taxera-teal" data-testid="tco-vendors-slider" />
                  <div className="flex justify-between text-2xs text-slate-400 mt-1"><span>1</span><span>15</span></div>
                </div>
                <div>
                  <label className="flex items-center justify-between text-sm font-semibold text-taxera-charcoal mb-2">Annual compliance spend (K EUR) <span className="text-taxera-teal font-bold">{annualSpend}K</span></label>
                  <input type="range" min={50} max={3000} step={50} value={annualSpend} onChange={e => setAnnualSpend(+e.target.value)} className="w-full accent-taxera-teal" data-testid="tco-spend-slider" />
                  <div className="flex justify-between text-2xs text-slate-400 mt-1"><span>50K</span><span>3M</span></div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <div className="text-2xs font-bold text-slate-400 uppercase tracking-wide mb-2">Estimated current TCO</div>
                  <div className="font-heading text-3xl font-bold text-slate-400 line-through">EUR {fmt(currentTCO)}</div>
                  <p className="text-xs text-slate-400 mt-1">Vendor fees + integration overhead + internal labor</p>
                </div>
                <div className="bg-taxera-teal/5 rounded-2xl border border-taxera-teal/20 p-5">
                  <div className="text-2xs font-bold text-taxera-teal uppercase tracking-wide mb-2">Projected TCO with Taxera</div>
                  <div className="font-heading text-3xl font-bold text-taxera-teal">EUR {fmt(taxeraTCO)}</div>
                  <p className="text-xs text-slate-500 mt-1">Single platform + reduced coordination</p>
                </div>
                <div className="bg-slate-900 rounded-2xl p-5 text-center">
                  <div className="text-2xs font-bold text-slate-400 uppercase tracking-wide mb-1">Estimated annual savings</div>
                  <div className="font-heading text-4xl font-bold text-white">EUR {fmt(Math.max(0, savings))}</div>
                  <div className="text-lg font-bold text-taxera-teal">{Math.max(0, savingsPct)}% reduction</div>
                </div>
                <p className="text-2xs text-slate-400 text-center">Illustrative model. Actual savings depend on configuration.</p>
              </div>
            </div>
          </div>
          <div className="flex justify-center gap-4 mt-8">
            <Link to="/sap-s4hana/assessment" className="group inline-flex items-center gap-2 px-6 py-3 taxera-gradient text-white rounded-full text-sm font-semibold hover:shadow-lg transition-all">Take the Readiness Assessment <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 text-taxera-charcoal rounded-full text-sm font-semibold hover:bg-slate-50">Talk to an Expert</Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default SapCalculatorPage;
